/**
 * <AdminAccess /> — dedicated Admin › Access panel for managing everyone
 * with elevated privileges (editors, admins, super-admins).
 *
 * Combines two data sources:
 *   • Users who currently hold an elevated `platform_role`.
 *   • The env allowlist (`SUPER_ADMIN_EMAILS`) — emails that will auto-
 *     promote on next sign-in, even if they haven't logged in yet.
 *
 * Super-admins can promote / demote / send a personal message here. Regular
 * admins get a read-only view (backend enforces the role-change gate).
 */
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Search, Users, Send, X, Loader2, Star, Pencil, Mail, Megaphone } from "lucide-react";
import AuditTrail from "@/components/admin/AuditTrail";
import BroadcastModal from "@/components/admin/BroadcastModal";

const ROLE_LABEL = {
  user: "User",
  editor: "Editor",
  admin: "Admin",
  organization_admin: "Org Admin",
  super_admin: "Super Admin",
};

const ROLE_COLOUR = {
  editor: "#0ea5e9",
  admin: "#8b5cf6",
  organization_admin: "#f59e0b",
  super_admin: "#dc2626",
};

export default function AdminAccess() {
  const { user: me } = useAuth();
  const isSuper = (me?.platform_role || (me?.is_admin ? "admin" : "user")) === "super_admin";
  const [roster, setRoster] = useState({ members: [], auto_promote_emails: [], pending_signin: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showMessage, setShowMessage] = useState(null); // user obj
  const [showPromote, setShowPromote] = useState(null); // user obj
  const [showBroadcast, setShowBroadcast] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/access/roster");
      setRoster(data);
    } catch (e) {
      toast.error("Couldn't load admin roster.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Debounced user search for promotion — hits /admin/users?search=
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSearchResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await http.get(`/admin/users?search=${encodeURIComponent(q)}&limit=15`);
        setSearchResults(data.items || []);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const changeRole = async (u, role) => {
    try {
      await http.patch(`/admin/users/${u.id}/role`, { platform_role: role });
      toast.success(`Role changed to ${ROLE_LABEL[role] || role}.`);
      setShowPromote(null);
      setSearch("");
      setSearchResults([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Only super-admins can change roles.");
    }
  };

  return (
    <div data-testid="admin-access" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <ShieldCheck size={12} className="inline -mt-0.5 mr-1" /> ACCESS
          </div>
          <h1 className="font-display text-3xl mt-1">Admins & editors</h1>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            {isSuper
              ? "Promote users to editor / admin / super-admin, or demote them back. Send a personal message from the actions column."
              : "Read-only for admins. Only super-admins can change roles."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowBroadcast(true)}
          className="wofy-btn-primary"
          data-testid="open-broadcast-btn"
        >
          <Megaphone className="w-4 h-4" /> Send broadcast
        </button>
      </div>

      {isSuper && (
        <div className="wofy-card">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              Search users to promote
            </span>
            <div className="relative mt-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="name, email, or phone…"
                className="wofy-input w-full pr-9"
                data-testid="access-search-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </span>
            </div>
          </label>
          {searchResults.length > 0 && (
            <div className="mt-3 rounded-lg border divide-y" style={{ borderColor: "var(--wofy-line)" }} data-testid="access-search-results">
              {searchResults.map((u) => {
                const role = u.platform_role || (u.is_admin ? "admin" : "user");
                return (
                  <div key={u.id} className="flex items-center gap-3 p-3" data-testid={`access-search-row-${u.id}`}>
                    <UserAvatar u={u} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{u.name || "—"}</div>
                      <div className="text-xs truncate" style={{ color: "var(--wofy-text-soft)" }}>{u.email || u.phone || u.id}</div>
                    </div>
                    <RoleBadge role={role} />
                    <button
                      onClick={() => setShowPromote({ ...u, currentRole: role })}
                      className="wofy-btn-ghost text-xs"
                      data-testid={`access-promote-${u.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Change role
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="wofy-card">
        <h2 className="font-display text-xl mb-3">
          <Users className="inline -mt-1 w-5 h-5 mr-2" />
          Current roster ({roster.members.length})
        </h2>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : roster.members.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nobody has an elevated role yet.</p>
        ) : (
          <div className="rounded-lg border divide-y" style={{ borderColor: "var(--wofy-line)" }} data-testid="access-roster">
            {roster.members.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-3" data-testid={`access-row-${u.id}`}>
                <UserAvatar u={u} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{u.name || "—"}</div>
                  <div className="text-xs truncate" style={{ color: "var(--wofy-text-soft)" }}>{u.email || u.phone || u.id}</div>
                </div>
                <RoleBadge role={u.role} />
                <button onClick={() => setShowMessage(u)} className="wofy-btn-ghost text-xs" data-testid={`access-message-${u.id}`}>
                  <Mail className="w-3 h-3" /> Message
                </button>
                {isSuper && u.id !== me?.id && (
                  <button onClick={() => setShowPromote({ ...u, currentRole: u.role })} className="wofy-btn-ghost text-xs" data-testid={`access-change-${u.id}`}>
                    <Pencil className="w-3 h-3" /> Change
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {roster.pending_signin.length > 0 && (
        <div className="wofy-card" data-testid="access-pending-signin">
          <h2 className="font-display text-xl mb-1">
            <Star className="inline -mt-1 w-5 h-5 mr-2" />
            Auto-promote allowlist ({roster.pending_signin.length} pending)
          </h2>
          <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
            These emails will auto-promote to <b>Super Admin</b> the next time they sign in. Configured via the <code>SUPER_ADMIN_EMAILS</code> env var — edit that and redeploy to change the list.
          </p>
          <div className="flex flex-wrap gap-1">
            {roster.pending_signin.map((e) => (
              <span key={e} className="text-xs rounded-full border px-2 py-1 font-mono" style={{ borderColor: "var(--wofy-line)" }}>{e}</span>
            ))}
          </div>
        </div>
      )}

      {showPromote && (
        <PromoteModal user={showPromote} onClose={() => setShowPromote(null)} onSave={(role) => changeRole(showPromote, role)} />
      )}
      {showMessage && (
        <MessageModal user={showMessage} onClose={() => setShowMessage(null)} onSent={() => { toast.success("Message delivered."); setShowMessage(null); }} />
      )}
      {showBroadcast && (
        <BroadcastModal onClose={() => setShowBroadcast(false)} />
      )}

      {/* Audit trail — every role change, admin message, moderation action,
          with actor + timestamp + reason. Keeps everyone accountable. */}
      <AuditTrail />
    </div>
  );
}

function UserAvatar({ u }) {
  const src = u.avatar_url || u.profile_picture;
  if (src) return <img src={src} alt={u.name} className="w-9 h-9 rounded-full object-cover" />;
  return (
    <div className="w-9 h-9 rounded-full text-white flex items-center justify-center font-bold text-sm" style={{ background: "var(--wofy-primary)" }}>
      {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function RoleBadge({ role }) {
  const clr = ROLE_COLOUR[role] || "#94a3b8";
  return (
    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full" style={{ background: `${clr}22`, color: clr }}>
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function PromoteModal({ user, onClose, onSave }) {
  const [role, setRole] = useState(user.currentRole || "user");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="promote-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>CHANGE ROLE</div>
            <h2 className="font-display text-xl mt-1">{user.name}</h2>
            <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{user.email || user.phone}</p>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {["user", "editor", "admin", "organization_admin", "super_admin"].map((r) => (
            <label key={r} className={`cursor-pointer rounded-lg border p-3 text-sm font-medium text-center ${role === r ? "text-white" : ""}`} style={role === r ? { background: "var(--wofy-primary)", borderColor: "transparent" } : { borderColor: "var(--wofy-border)" }}>
              <input type="radio" name="new-role" checked={role === r} onChange={() => setRole(r)} className="sr-only" data-testid={`promote-role-${r}`} />
              {ROLE_LABEL[r]}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <button onClick={onClose} className="wofy-btn-ghost">Cancel</button>
          <button onClick={() => onSave(role)} disabled={role === user.currentRole} className="wofy-btn-primary" data-testid="promote-save">
            {role === user.currentRole ? "No change" : `Set as ${ROLE_LABEL[role]}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageModal({ user, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) { toast.error("Write a message first."); return; }
    setBusy(true);
    try {
      await http.post(`/admin/users/${user.id}/message`, { subject: subject.trim() || undefined, message: body.trim() });
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="message-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl p-6 space-y-4" style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>MESSAGE</div>
            <h2 className="font-display text-xl mt-1">To: {user.name}</h2>
            <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{user.email || user.phone}</p>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost"><X className="w-4 h-4" /></button>
        </div>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Subject (optional)</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} className="wofy-input mt-1" placeholder="A message from Social Pulse" data-testid="message-subject" />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Message</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={5000} className="wofy-input mt-1" placeholder="Write a message — delivered to their dashboard inbox and emailed if they have an address on file." data-testid="message-body" />
        </label>
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          Delivered to the user's admin-inbox on their dashboard AND emailed via Resend if they have an email on file. Replies go to <b>support@socialpulse.com</b>.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--wofy-line)" }}>
          <button onClick={onClose} className="wofy-btn-ghost">Cancel</button>
          <button onClick={send} disabled={busy || !body.trim()} className="wofy-btn-primary" data-testid="message-send">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
